import { useToastManager } from "@porsche-design-system/components-react"
import type {
	ChangePerformanceKitView,
	ChangeWheelsView,
	DetailsEquipmentView,
	EquipmentOptionView,
} from "@slfinrtl/ui-bff-routes"
import { changeEquipmentRoute, resetToFactoryEquipmentIncludingWheelsRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useState } from "react"
import { useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import type { ConfirmEquipmentResetModalProps } from "src/components"
import { ConfirmEquipmentResetModal } from "src/components"
import { useModal } from "src/context/modal/useModal"
import { useBff, useBffDynamic } from "src/hooks"
import { useVehicleDetailsContext } from "src/screens/vehicleDetails/context/VehicleDetailsContext"
import type { SpecialEquipmentEditProps } from "../../components/specialEquipmentEdit/SpecialEquipmentEdit"
import { useChangePerformanceKit } from "../useChangePerformanceKit/useChangePerformanceKit"
import { useChangeWheels } from "../useChangeWheels/useChangeWheels"
import { useSpecialEquipmentListEdit } from "../useSpecialEquipmentListEdit/useSpecialEquipmentListEdit"

export interface EditEquipmentView {
	equipmentList: EquipmentOptionView[]
	performanceKit: ChangePerformanceKitView
	wheels: ChangeWheelsView
}

export interface UseSpecialEquipmentEditProps {
	leaveEditStage: (viewUpdate: DetailsEquipmentView | undefined) => void
	view: EditEquipmentView
}

export const useSpecialEquipmentEdit = ({
	leaveEditStage,
	view,
}: UseSpecialEquipmentEditProps): SpecialEquipmentEditProps => {
	const { formatMessage } = useIntl()

	// TODO: change this to boolean and track "reset" or "save" mode separately
	const [submitting, setSubmitting] = useState<false | "save" | "reset">(false)
	const [hasSubmitFailed, setHasSubmitFailed] = useState<boolean>(false)

	const { inventoryId } = useVehicleDetailsContext()
	const { addMessage } = useToastManager()

	const {
		custom: changeCustomWheels,
		getChange: getWheelsChange,
		selected: changeSelectedWheels,
	} = useChangeWheels({ view: view.wheels })

	const { formWithProps: changePerformanceKit, getChange: getPerformanceKitChange } = useChangePerformanceKit({
		view: view.performanceKit,
	})

	const {
		getChange: getEquipmentListChange,
		resetChanges: resetEquipmentListChanges,
		view: equipmentListView,
	} = useSpecialEquipmentListEdit({ view: view.equipmentList })

	const resetToFactoryEquipment = useBff(
		resetToFactoryEquipmentIncludingWheelsRoute,
		{
			body: undefined,
			pathParams: {},
			queryParams: {
				"inventory-id": inventoryId,
			},
		},
		"inline",
	)

	const onResetToFactoryEquipment = useCallback(() => {
		setSubmitting("reset")
		return resetToFactoryEquipment()
			.then(leaveEditStage)
			.catch(() => setHasSubmitFailed(true))
			.finally(() => setSubmitting(false))
	}, [leaveEditStage, resetToFactoryEquipment])

	const showConfirmEquipmentResetModal = useModal<ConfirmEquipmentResetModalProps>(ConfirmEquipmentResetModal)
	const handleShowConfirmEquipmentResetModal = useCallback(
		() => showConfirmEquipmentResetModal({ onResetToFactoryEquipment }),
		[onResetToFactoryEquipment, showConfirmEquipmentResetModal],
	)

	const changeEquipment = useBffDynamic(changeEquipmentRoute, "inline")

	const save = useCallback(() => {
		setSubmitting("save")
		return changeEquipment({
			body: {
				...getWheelsChange(),
				...getEquipmentListChange(),
				...getPerformanceKitChange(),
			},
			pathParams: {},
			queryParams: {
				"inventory-id": inventoryId,
			},
		})
			.then((detailsEquipmentView) => {
				resetEquipmentListChanges()
				leaveEditStage(detailsEquipmentView)
				addMessage({
					state: "success",
					text: formatMessage(commonMessages.saveSuccess),
				})
			})
			.catch(() => setHasSubmitFailed(true))
			.finally(() => setSubmitting(false))
	}, [
		changeEquipment,
		inventoryId,
		getWheelsChange,
		getEquipmentListChange,
		getPerformanceKitChange,
		resetEquipmentListChanges,
		leaveEditStage,
		addMessage,
		formatMessage,
	])

	const onSave = changeSelectedWheels.form.handleSubmit(async () => {
		let submittedSave = save
		if (changeCustomWheels) {
			submittedSave = changeCustomWheels.form.handleSubmit(submittedSave)
		}
		submittedSave = changePerformanceKit.form.handleSubmit(submittedSave)
		return submittedSave()
	})

	const onCancel = useCallback(() => leaveEditStage(undefined), [leaveEditStage])

	return {
		changeCustomWheels,
		changePerformanceKit,
		changeSelectedWheels,
		editableEquipmentListProps: equipmentListView,
		hasSubmitFailed,
		onCancel,
		onResetToFactoryEquipment: handleShowConfirmEquipmentResetModal,
		onSave,
		setHasSubmitFailed,
		submitting,
	}
}
